import { report } from '../report';
import Signal from './Signal';
import { sleep } from 'Utility/async';

export default class Reasons<T extends string> {
    public readonly hasAnyChangedSignal = new Signal<[boolean]>();
    private readonly reasons = new Map<T, number>();

    constructor(initialOnChangeListener?: (reasonExists: boolean) => void, executeOnCreate?: boolean) {
        if (initialOnChangeListener) {
            this.addListener(initialOnChangeListener, executeOnCreate);
        }
    }

    public addListener(listener: (reasonExists: boolean) => void, executeImmediatly?: boolean) {
        this.hasAnyChangedSignal.addListener(listener);
        if (executeImmediatly) {
            listener(this.hasAny());
        }
    }

    public setPresence(reason: T, present: boolean) {
        if (present) {
            this.add(reason);
        } else {
            this.remove(reason);
        }
    }

    public add(reason: T, increment: boolean = false) {
        const hadAny = this.hasAny();
        this.reasons.set(reason, increment ? (this.reasons.get(reason) || 0) + 1 : this.reasons.get(reason) || 1);
        if (!hadAny && this.hasAny()) {
            this.hasAnyChangedSignal.dispatch(true);
        }
    }

    public remove(reason: T, decrement: boolean = false) {
        const hadAny = this.hasAny();
        if (decrement) {
            const newCount = (this.reasons.get(reason) || 0) - 1;
            if (newCount < 0) {
                report('Reasons count went below 0', {
                    once: true,
                    tags: { reason },
                });
            }
            this.reasons.set(reason, newCount);
        } else {
            this.reasons.delete(reason);
        }
        if (hadAny && !this.hasAny()) {
            this.hasAnyChangedSignal.dispatch(false);
        }
        return !this.has(reason);
    }

    public removeAll() {
        const hadAny = this.hasAny();
        this.reasons.clear();
        if (hadAny) {
            this.hasAnyChangedSignal.dispatch(false);
        }
    }

    public has(reason: T) {
        return this.reasons.has(reason) && this.reasons.get(reason)! > 0;
    }

    public hasAny() {
        return !this.hasNone();
    }

    public hasNone() {
        return this.getActiveReasonsCount() === 0;
    }

    public async untilHas(reason: T): Promise<void> {
        while (this.has(reason)) {
            await sleep(1);
        }
    }

    public toString() {
        return Array.from(this.reasons.keys())
            .map((reason: T) => reason.toString())
            .join(', ');
    }

    private getActiveReasonsCount() {
        let count = 0;
        this.reasons.forEach(value => {
            if (value > 0) count += 1;
        });
        return count;
    }
}
